import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API, { jobsAPI, contactAPI, applicationsAPI } from '../utils/api';
import toast from 'react-hot-toast';
import {
  FaBriefcase, FaUsers, FaEnvelope, FaChartBar, FaPlus, FaEdit,
  FaTrash, FaTimes, FaCheck, FaToggleOn, FaToggleOff, FaUpload, FaEye,
  FaSignOutAlt, FaWhatsapp
} from 'react-icons/fa';
import './Admin.css';

const COUNTRIES = ['UAE', 'Saudi Arabia', 'Kuwait', 'Qatar', 'Bahrain', 'Oman', 'Singapore', 'Malaysia', 'Libya'];
const CATEGORIES = ['Engineering', 'Healthcare', 'Hospitality', 'Labour', 'Technology', 'Finance', 'Logistics', 'Transport', 'Education', 'Agriculture', 'Construction', 'Administration', 'Security', 'Automotive', 'Sales', 'Retail'];
const FOOD_OPTIONS = ['Provided', 'Not Provided', 'Partially Provided'];
const STATUS_OPTIONS = ['Pending','Warm', 'SemiWarm', 'NAC', 'Cold', 'Rejected', 'Hold'];        

const emptyJob = { job_role: '', category: '', country: '', salary: '', num_vacancies: 1, food_accommodation: 'Not Provided', additional_details: '', is_active: true };

const Admin = () => {
  const { user, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({});
  const [jobs, setJobs] = useState([]);
  const [messages, setMessages] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [jobForm, setJobForm] = useState(emptyJob);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [completedApps, setCompletedApps] = useState([]);
  const [appFilter, setAppFilter] = useState({ name: '', job_role: '', country: '', date: '' });

  useEffect(() => {
    if (!isAdmin) { navigate('/login'); return; }
    fetchAll();
  }, [isAdmin]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, jobsRes, msgsRes, appsRes] = await Promise.all([
        applicationsAPI.getStats(),
        jobsAPI.adminGetAll(),
        contactAPI.getAll(),
        applicationsAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setJobs(jobsRes.data);
      setMessages(msgsRes.data);
      setApplications(appsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const openAddJob = () => { setEditingJob(null); setJobForm(emptyJob); setImageFile(null); setImagePreview(''); setShowJobModal(true); };
  const openEditJob = (job) => { setEditingJob(job); setJobForm({ ...job, is_active: !!job.is_active }); setImageFile(null); setImagePreview(job.image_url ? `http://localhost:5000${job.image_url}` : ''); setShowJobModal(true); };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }
  };

  const handleJobSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(jobForm).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append('image', imageFile);
      if (editingJob) { await jobsAPI.update(editingJob.id, fd); toast.success('Job updated!'); }
      else { await jobsAPI.create(fd); toast.success('Job created!'); }
      setShowJobModal(false);
      fetchAll();
    } catch { toast.error('Failed to save job'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteJob = async (id) => {
    if (!window.confirm('Delete this job vacancy?')) return;
    try { await jobsAPI.delete(id); toast.success('Job deleted'); fetchAll(); }
    catch { toast.error('Failed to delete job'); }
  };

  const handleMsgStatus = async (id, status) => {
    try { await contactAPI.updateStatus(id, status); fetchAll(); }
    catch { toast.error('Failed to update status'); }
  };

  const handleAppStatus = async (id, status) => {
    try { await applicationsAPI.updateStatus(id, status); fetchAll(); }
    catch { toast.error('Failed to update status'); }
  };

  const handleDeleteApp = async (id) => {
    if (!window.confirm('Delete this application?')) return;
    try {
      await API.delete(`/applications/${id}`);
      toast.success('Application deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete application');
    }
  };

  const filteredApps = applications.filter(a => {
    const matchName = appFilter.name.trim() === '' ||
      a.name.toLowerCase().includes(appFilter.name.toLowerCase());
    const matchJob = appFilter.job_role.trim() === '' ||
      a.job_role.toLowerCase().includes(appFilter.job_role.toLowerCase());
    const matchCountry = appFilter.country === '' || a.country === appFilter.country;
    const matchDate = appFilter.date === '' ||
      new Date(a.created_at).toISOString().slice(0, 10) === appFilter.date;
    return matchName && matchJob && matchCountry && matchDate;
  });

  const TABS = [
    { id: 'dashboard', label: 'Dashboard', icon: <FaChartBar /> },
    { id: 'jobs', label: 'Job Vacancies', icon: <FaBriefcase /> },
    { id: 'applications', label: 'Applications', icon: <FaUsers /> },
    { id: 'messages', label: 'Messages', icon: <FaEnvelope /> },
  ];

  return (
    <div className="admin-page">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-logo">
          <div className="admin-logo-icon">P</div>
          <div>
            <div className="admin-logo-name">Pavishna</div>
            <div className="admin-logo-sub">Admin Panel</div>
          </div>
        </div>
        <nav className="admin-nav">
          {TABS.map(t => (
            <button key={t.id} className={`admin-nav-item${activeTab === t.id ? ' active' : ''}`} onClick={() => setActiveTab(t.id)}>
              {t.icon} {t.label}
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-footer">
          <div className="admin-user-info">
            <div className="admin-avatar">{user?.name?.[0] || 'A'}</div>
            <div>
              <div className="admin-user-name">{user?.name}</div>
              <div className="admin-user-role">Administrator</div>
            </div>
          </div>
          <button className="admin-logout-btn" onClick={() => { logout(); navigate('/login'); }}>
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-topbar">
          <h2 className="admin-page-title">
            {TABS.find(t => t.id === activeTab)?.label}
          </h2>
          {activeTab === 'jobs' && (
            <button className="btn-primary" onClick={openAddJob}><FaPlus /> Add Job Vacancy</button>
          )}
        </div>

        <div className="admin-content">
          {loading && <div className="loading-wrap"><div className="spinner" /></div>}

          {/* Dashboard */}
          {activeTab === 'dashboard' && !loading && (
            <div>
              <div className="stats-cards">
                <div className="stat-card"><div className="sc-icon jobs"><FaBriefcase /></div><div><div className="sc-value">{stats.total_jobs || 0}</div><div className="sc-label">Active Jobs</div></div></div>
                <div className="stat-card"><div className="sc-icon apps"><FaUsers /></div><div><div className="sc-value">{stats.total_applications || 0}</div><div className="sc-label">Applications</div></div></div>
                <div className="stat-card"><div className="sc-icon msgs"><FaEnvelope /></div><div><div className="sc-value">{stats.total_messages || 0}</div><div className="sc-label">Unread Messages</div></div></div>
                <div className="stat-card"><div className="sc-icon users"><FaChartBar /></div><div><div className="sc-value">{stats.total_users || 0}</div><div className="sc-label">Registered Users</div></div></div>
              </div>
              <div className="quick-actions">
                <h3>Quick Actions</h3>
                <div className="qa-grid">
                  <button className="qa-btn" onClick={() => { setActiveTab('jobs'); setTimeout(openAddJob, 100); }}><FaPlus /><span>Add Job Vacancy</span></button>
                  <button className="qa-btn" onClick={() => setActiveTab('applications')}><FaUsers /><span>View Applications</span></button>
                  <button className="qa-btn" onClick={() => setActiveTab('messages')}><FaEnvelope /><span>Read Messages</span></button>
                </div>
              </div>
              <div className="recent-jobs-widget">
                <h3>Recent Job Vacancies</h3>
                <table className="admin-table">
                  <thead><tr><th>Job Role</th><th>Country</th><th>Vacancies</th><th>Status</th></tr></thead>
                  <tbody>
                    {jobs.slice(0, 6).map(j => (
                      <tr key={j.id}>
                        <td><strong>{j.job_role}</strong></td>
                        <td>{j.country}</td>
                        <td>{j.num_vacancies}</td>
                        <td><span className={`status-badge ${j.is_active ? 'active' : 'inactive'}`}>{j.is_active ? 'Active' : 'Inactive'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && !loading && (
            <div>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr><th>Image</th><th>Job Role</th><th>Country</th><th>Salary</th><th>Vacancies</th><th>Food & Stay</th><th>Status</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {jobs.map(j => (
                      <tr key={j.id}>
                        <td><div className="table-img-wrap">{j.image_url ? <img src={`http://localhost:5000${j.image_url}`} alt="" /> : <div className="no-img"><FaBriefcase /></div>}</div></td>
                        <td><strong>{j.job_role}</strong><br /><small>{j.category}</small></td>
                        <td>{j.country}</td>
                        <td>{j.salary || '—'}</td>
                        <td>{j.num_vacancies}</td>
                        <td><span className={`food-tag ${j.food_accommodation === 'Provided' ? 'yes' : j.food_accommodation === 'Partially Provided' ? 'partial' : 'no'}`}>{j.food_accommodation}</span></td>
                        <td><span className={`status-badge ${j.is_active ? 'active' : 'inactive'}`}>{j.is_active ? 'Active' : 'Inactive'}</span></td>
                        <td>
                          <div className="action-btns">
                            <button className="action-btn edit" onClick={() => openEditJob(j)} title="Edit"><FaEdit /></button>
                            <button className="action-btn delete" onClick={() => handleDeleteJob(j.id)} title="Delete"><FaTrash /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {jobs.length === 0 && <div className="empty-table">No job vacancies yet. <button onClick={openAddJob} className="link-btn">Add one</button></div>}
              </div>
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && !loading && (
            <div>
              {/* Filter Panel */}
              <div className="filter-panel">
                <div className="filter-item">
                  <label>Name</label>
                  <input
                    className="filter-input"
                    placeholder="Search name..."
                    value={appFilter.name}
                    onChange={e => setAppFilter({...appFilter, name: e.target.value})}
                  />
                </div>
                <div className="filter-item">
                  <label>Job Role</label>
                  <input
                    className="filter-input"
                    placeholder="Search job role..."
                    value={appFilter.job_role}
                    onChange={e => setAppFilter({...appFilter, job_role: e.target.value})}
                  />
                </div>
                <div className="filter-item">
                  <label>Country</label>
                  <select
                    className="filter-input"
                    value={appFilter.country}
                    onChange={e => setAppFilter({...appFilter, country: e.target.value})}
                  >
                    <option value="">All Countries</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="filter-item">
                  <label>Date</label>
                  <input
                    type="date"
                    className="filter-input"
                    value={appFilter.date}
                    onChange={e => setAppFilter({...appFilter, date: e.target.value})}
                  />
                </div>
                <button
                  className="filter-clear-btn"
                  onClick={() => setAppFilter({ name: '', job_role: '', country: '', date: '' })}
                >
                  <FaTimes /> Clear
                </button>
              </div>

              {/* Results Count */}
              <div className="filter-results-count">
                Showing <strong>{filteredApps.length}</strong> of <strong>{applications.length}</strong> applications
              </div>

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Applicant</th>
                      <th>Job Role</th>
                      <th>Country</th>
                      <th>Contact</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Resume</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredApps.map(a => (
                      <tr key={a.id} className={completedApps.includes(a.id) ? 'row-completed' : ''}>
                        <td><strong>{a.name}</strong></td>
                        <td>{a.job_role}</td>
                        <td>{a.country}</td>
                        <td><small>{a.email}<br />{a.phone}</small></td>
                        <td>
                          <select
                            className="status-select"
                            value={a.status}
                            onChange={e => handleAppStatus(a.id, e.target.value)}
                          >
                            {STATUS_OPTIONS.map(s => (
                              <option key={s} value={s}>
                                {s.charAt(0).toUpperCase() + s.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td><small>{new Date(a.created_at).toLocaleDateString()}</small></td>

                        {/* Resume Column */}
                        <td>
                          {a.resume_url ? (
                            <a
                              href={`http://localhost:5000${a.resume_url}`}
                              target="_blank"
                              rel="noreferrer"
                              download
                              className="resume-download-btn"
                              title="Download Resume"
                            >
                              📄 Download
                            </a>
                          ) : (
                            <span style={{ color: '#aaa', fontSize: 12 }}>No resume</span>
                          )}
                        </td>

                        {/* Actions Column */}
                        <td>
                          <div className="action-btns">
                            {a.message && (
                              <button
                                className="action-btn view"
                                onClick={() => alert(`Message from ${a.name}:\n\n${a.message}`)}
                                title="View Message"
                              >
                                <FaEye />
                              </button>
                            )}
                            {a.phone && (
                              <a
                                href={`https://wa.me/${a.phone.replace(/[^0-9]/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                                className="action-btn edit"
                                title="WhatsApp"
                              >
                                <FaWhatsapp />
                              </a>
                            )}
                            <button
                              className="action-btn complete"
                              onClick={() => {
                                setCompletedApps(prev =>
                                  prev.includes(a.id)
                                    ? prev.filter(id => id !== a.id)
                                    : [...prev, a.id]
                                );
                                handleAppStatus(a.id, 'shortlisted');
                                toast.success(`${a.name}'s application marked as completed`);
                              }}
                              title="Mark Completed"
                            >
                              <FaCheck />
                            </button>
                            <button
                              className="action-btn delete"
                              onClick={() => handleDeleteApp(a.id)}
                              title="Delete"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredApps.length === 0 && (
                  <div className="empty-table">
                    {applications.length === 0 ? 'No applications yet.' : 'No applications match your filters.'}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Messages Tab */}
          {activeTab === 'messages' && !loading && (
            <div className="messages-list">
              {messages.map(m => (
                <div key={m.id} className={`msg-card ${m.status}`}>
                  <div className="msg-header">
                    <div>
                      <strong>{m.name}</strong>
                      <span className="msg-email">{m.email} {m.phone && `· ${m.phone}`}</span>
                    </div>
                    <div className="msg-meta">
                      <span className={`status-badge ${m.status}`}>{m.status}</span>
                      <span className="msg-date">{new Date(m.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <p className="msg-body">{m.message}</p>
                  <div className="msg-actions">
                    <button className="action-btn-sm" onClick={() => handleMsgStatus(m.id, 'read')}><FaCheck /> Mark Read</button>
                    <button className="action-btn-sm" onClick={() => handleMsgStatus(m.id, 'replied')}><FaEnvelope /> Mark Replied</button>
                    <a href={`mailto:${m.email}`} className="action-btn-sm reply-btn">Reply via Email</a>
                  </div>
                </div>
              ))}
              {messages.length === 0 && <div className="empty-table">No messages yet.</div>}
            </div>
          )}
        </div>
      </main>

      {/* Job Modal */}
      {showJobModal && (
        <div className="modal-overlay" onClick={() => setShowJobModal(false)}>
          <div className="modal-box job-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowJobModal(false)}><FaTimes /></button>
            <h2 className="modal-title-h">{editingJob ? 'Edit Job Vacancy' : 'Add New Job Vacancy'}</h2>
            <form onSubmit={handleJobSubmit} className="job-form">
              <div className="img-upload-area" onClick={() => document.getElementById('job-img-inp').click()}>
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="img-preview" />
                ) : (
                  <div className="img-placeholder"><FaUpload /><span>Click to upload job image</span><small>JPG, PNG up to 5MB</small></div>
                )}
                <input id="job-img-inp" type="file" accept="image/*" onChange={handleImageChange} style={{ display: 'none' }} />
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label>Job Role *</label>
                  <input className="form-control" value={jobForm.job_role} onChange={e => setJobForm({...jobForm, job_role: e.target.value})} required placeholder="e.g. Civil Engineer" />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select className="form-control" value={jobForm.category} onChange={e => setJobForm({...jobForm, category: e.target.value})}>
                    <option value="">Select category</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Country *</label>
                  <select className="form-control" value={jobForm.country} onChange={e => setJobForm({...jobForm, country: e.target.value})} required>
                    <option value="">Select country</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Salary</label>
                  <input className="form-control" value={jobForm.salary} onChange={e => setJobForm({...jobForm, salary: e.target.value})} placeholder="e.g. AED 3,000 - 5,000" />
                </div>
              </div>
              <div className="form-row-2">
                <div className="form-group">
                  <label>Number of Vacancies</label>
                  <input type="number" min="1" className="form-control" value={jobForm.num_vacancies} onChange={e => setJobForm({...jobForm, num_vacancies: parseInt(e.target.value)})} />
                </div>
                <div className="form-group">
                  <label>Food & Accommodation</label>
                  <select className="form-control" value={jobForm.food_accommodation} onChange={e => setJobForm({...jobForm, food_accommodation: e.target.value})}>
                    {FOOD_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Additional Details / Requirements</label>
                <textarea className="form-control" rows={4} value={jobForm.additional_details} onChange={e => setJobForm({...jobForm, additional_details: e.target.value})} placeholder="Experience required, qualifications, special requirements, etc." />
              </div>
              <div className="form-group toggle-group">
                <label>Status</label>
                <div className="toggle-wrap">
                  <button type="button" className={`toggle-btn ${jobForm.is_active ? 'on' : 'off'}`} onClick={() => setJobForm({...jobForm, is_active: !jobForm.is_active})}>
                    {jobForm.is_active ? <FaToggleOn /> : <FaToggleOff />}
                    {jobForm.is_active ? ' Active (Visible)' : ' Inactive (Hidden)'}
                  </button>
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowJobModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingJob ? <><FaEdit /> Update Job</> : <><FaPlus /> Create Job</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;